import type { ReactNode } from 'react'
import React, { useEffect, useRef } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuthUser, useAuthStatus } from '../../contexts/AuthContext'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { computeSessionExpired, getRedirectState } from './utils/sessionState'
import { getIntendedRoute, clearIntendedRoute } from './utils/routePreservation'
import { AUTHENTICATED_NAMESPACES } from '@/i18n/publicResources'

interface ProtectedRouteProps {
    children: ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const user = useAuthUser()
    const { status, hydrated, isIntentionalLogout } = useAuthStatus()
    const location = useLocation()
    const navigate = useNavigate()
    const { t, i18n } = useTranslation()
    const hasNavigatedRef = useRef(false)

    // Compute session expired status using extracted utility
    const sessionExpired = computeSessionExpired(hydrated, status, user)

    // Warm the authenticated-only locale namespaces once signed in. Under
    // lazy locale loading (TRA-115) components that use prefixed keys
    // (t('review:...')) without declaring the namespace would otherwise
    // render raw keys until something else loaded that namespace.
    useEffect(() => {
        if (status === 'signedIn') {
            void i18n.loadNamespaces([...AUTHENTICATED_NAMESPACES])
        }
    }, [status, i18n])

    // Preserve route after successful rehydration
    // This must run early to prevent other redirects (like LandingPage) from interfering
    useEffect(() => {
        // Only navigate once when transitioning from 'restoring' to 'signedIn'
        if (hydrated && status === 'signedIn' && !hasNavigatedRef.current) {
            const intendedRoute = getIntendedRoute()
            if (intendedRoute) {
                // Always navigate to preserved route to ensure we stay on it
                // This prevents other redirects from interfering, even if paths match
                // React Router's navigate with replace is safe even if already on the route
                hasNavigatedRef.current = true
                clearIntendedRoute()
                void navigate(intendedRoute, { replace: true })
            } else {
                // No preserved route, mark as handled
                hasNavigatedRef.current = true
            }
        }
        // Reset ref when status changes away from 'signedIn' (e.g., logout)
        if (status !== 'signedIn') {
            hasNavigatedRef.current = false
        }
    }, [hydrated, status, location, navigate])

    // Don't render anything until auth is hydrated
    if (!hydrated) {
        return null
    }

    // If restoring session, show inline loading
    if (status === 'restoring') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: (theme) => theme.spacing(25), // 200px = 25 * 8px
                    p: 3,
                }}
                role="status"
                aria-live="polite"
                aria-label={t('common:restoringSession')}
            >
                <CircularProgress size={24} sx={{ mb: 2 }} />
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    {t('common:restoringSession')}
                </Typography>
            </Box>
        )
    }

    // If signed out after hydration, redirect to login
    if (status === 'signedOut') {
        // Use extracted utility to compute redirect state
        const redirectState = getRedirectState(isIntentionalLogout, sessionExpired, location)
        return <Navigate to="/login" state={redirectState} replace />
    }

    // If signed in but the subscription is inactive, route directly to the
    // account-inactive page. Mirrors the backend's SUBSCRIPTION_INACTIVE gate
    // so we don't briefly render a protected page before the interceptor 403s.
    if (user?.has_active_subscription === false && location.pathname !== '/account-inactive') {
        return <Navigate to="/account-inactive" replace />
    }

    // If signed in, render the protected content
    return <>{children}</>
}

export default ProtectedRoute
